import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDropzone } from "react-dropzone";
import { toast } from "sonner";
import { UploadCloud, X, FileText, Image as ImageIcon, CheckCircle, AlertCircle, Loader2 } from "lucide-react";
import { API, formatApiError } from "../lib/api";
import axios from "axios";
import useReveal from "../hooks/useReveal";

const MAX_FILES = 5;
const MAX_SIZE = 10 * 1024 * 1024;
const ACCEPTED = {
    "application/pdf": [".pdf"],
    "application/msword": [".doc"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
    "image/jpeg": [".jpg", ".jpeg"],
    "image/png": [".png"],
};

const FALLBACK_POSITIONS = ["Marketing & Organisation", "Bauhof Allrounder", "Elektriker / Anlagenmonteur", "Initiativbewerbung"];

const initialForm = {
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    city: "",
    position: "",
    message: "",
    privacy: false,
    website: "", // honeypot
};

function formatBytes(b) {
    if (b < 1024) return `${b} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1024 / 1024).toFixed(2)} MB`;
}

function fileIcon(name) {
    const ext = name.split(".").pop().toLowerCase();
    if (["jpg", "jpeg", "png"].includes(ext)) return ImageIcon;
    return FileText;
}

export default function ApplicationForm() {
    const ref = useReveal();
    const [form, setForm] = useState(initialForm);
    const [files, setFiles] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);
    const [errors, setErrors] = useState({});
    const [positions, setPositions] = useState(FALLBACK_POSITIONS);

    useEffect(() => {
        axios
            .get(`${API}/jobs`)
            .then(({ data }) => {
                const p = (data.items || []).map((j) => j.position);
                if (p.length) setPositions(p);
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        const handler = (e) => {
            setForm((f) => ({ ...f, position: e.detail }));
            setTimeout(() => {
                const el = document.querySelector('[data-testid="form-position"]');
                if (el) el.focus({ preventScroll: true });
            }, 400);
        };
        window.addEventListener("moebus:setPosition", handler);
        return () => window.removeEventListener("moebus:setPosition", handler);
    }, []);

    const onDrop = useCallback(
        (accepted, rejected) => {
            const newFiles = [...files];
            rejected.forEach((r) => {
                const err = r.errors?.[0];
                if (err?.code === "file-too-large") {
                    toast.error(`Datei "${r.file.name}" ist größer als 10 MB.`);
                } else if (err?.code === "file-invalid-type") {
                    toast.error(`Datei "${r.file.name}": Typ nicht erlaubt.`);
                } else {
                    toast.error(`Datei "${r.file.name}" abgelehnt.`);
                }
            });
            accepted.forEach((f) => {
                if (newFiles.length >= MAX_FILES) {
                    toast.error(`Maximal ${MAX_FILES} Dateien.`);
                    return;
                }
                if (!newFiles.find((x) => x.name === f.name && x.size === f.size)) {
                    newFiles.push(f);
                }
            });
            setFiles(newFiles);
        },
        [files]
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: ACCEPTED,
        maxSize: MAX_SIZE,
        maxFiles: MAX_FILES,
    });

    const removeFile = (idx) => setFiles((arr) => arr.filter((_, i) => i !== idx));

    const update = (k, v) => {
        setForm((f) => ({ ...f, [k]: v }));
        if (errors[k]) setErrors((e) => ({ ...e, [k]: null }));
    };

    const validate = () => {
        const errs = {};
        if (!form.first_name.trim()) errs.first_name = "Vorname ist erforderlich.";
        if (!form.last_name.trim()) errs.last_name = "Nachname ist erforderlich.";
        if (!form.phone.trim()) errs.phone = "Telefonnummer ist erforderlich.";
        if (!form.email.trim()) errs.email = "E-Mail ist erforderlich.";
        else if (!/^\S+@\S+\.\S+$/.test(form.email)) errs.email = "Ungültige E-Mail-Adresse.";
        if (!form.city.trim()) errs.city = "Wohnort ist erforderlich.";
        if (!form.position) errs.position = "Bitte einen Bereich wählen.";
        if (!form.privacy) errs.privacy = "Bitte Datenschutz akzeptieren.";
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) {
            toast.error("Bitte alle Pflichtfelder korrekt ausfüllen.");
            return;
        }
        setSubmitting(true);
        try {
            const fd = new FormData();
            fd.append("first_name", form.first_name.trim());
            fd.append("last_name", form.last_name.trim());
            fd.append("phone", form.phone.trim());
            fd.append("email", form.email.trim());
            fd.append("city", form.city.trim());
            fd.append("position", form.position);
            fd.append("message", form.message);
            fd.append("privacy_consent", "true");
            fd.append("website", form.website || "");
            files.forEach((f) => fd.append("files", f));

            await axios.post(`${API}/applications`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
                timeout: 180000, // 3 min for large uploads
            });
            setDone(true);
            window.scrollTo({ top: document.getElementById("bewerbung").offsetTop - 72, behavior: "smooth" });
        } catch (err) {
            let msg;
            if (err.code === "ECONNABORTED") {
                msg = "Zeitüberschreitung. Bitte prüfe deine Verbindung und versuche es erneut – oder verwende kleinere Dateien.";
            } else if (!err.response) {
                msg = "Netzwerk-Fehler. Bitte prüfe deine Verbindung und versuche es erneut.";
            } else {
                const status = err.response.status;
                const detail = err?.response?.data?.detail;
                if (detail) {
                    msg = formatApiError(detail);
                } else if (status === 413) {
                    msg = "Dateien zu groß. Bitte reduziere die Größe oder Anzahl der Anhänge.";
                } else if (status === 502 || status === 503 || status === 504) {
                    msg = "Server aktuell nicht erreichbar. Bitte in ein paar Minuten erneut versuchen.";
                } else if (status >= 500) {
                    msg = `Server-Fehler (${status}). Bitte kurz warten und erneut versuchen.`;
                } else {
                    msg = `Fehler ${status}. Bitte versuche es erneut.`;
                }
            }
            console.error("Application submit error:", err);
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    const inputCls = (key) =>
        `w-full bg-white border ${
            errors[key] ? "border-red-500" : "border-moebus-border"
        } rounded-md px-4 py-3 text-base text-moebus-deep placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-moebus-green focus:border-transparent transition`;

    if (done) {
        return (
            <section
                id="bewerbung"
                data-testid="confirmation-section"
                className="py-24 md:py-32 bg-moebus-surface"
            >
                <div className="container mx-auto px-4 md:px-8">
                    <div className="max-w-2xl mx-auto bg-white border border-moebus-border rounded-lg p-10 md:p-14 text-center shadow-lg">
                        <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-6">
                            <CheckCircle size={44} className="text-moebus-green" strokeWidth={1.8} />
                        </div>
                        <h2 className="font-heading font-bold text-3xl md:text-4xl text-moebus-deep mb-4">
                            Vielen Dank für deine Bewerbung.
                        </h2>
                        <p className="text-base md:text-lg text-slate-600 leading-relaxed">
                            Wir haben deine Unterlagen erhalten und melden uns schnellstmöglich bei dir. Eine
                            Bestätigung haben wir an deine E-Mail-Adresse gesendet.
                        </p>
                        <button
                            data-testid="reset-form-btn"
                            onClick={() => {
                                setForm(initialForm);
                                setFiles([]);
                                setDone(false);
                            }}
                            className="mt-8 text-sm font-medium text-moebus-green hover:underline"
                        >
                            Weitere Bewerbung senden
                        </button>
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section
            id="bewerbung"
            data-testid="application-form-section"
            ref={ref}
            className="reveal py-20 md:py-28 bg-moebus-surface relative overflow-hidden"
        >
            <div className="absolute inset-0 grid-pattern opacity-40"></div>
            <div className="relative container mx-auto px-4 md:px-8">
                <div className="max-w-3xl mx-auto mb-12 text-center">
                    <div className="text-xs tracking-[0.3em] uppercase text-moebus-green font-semibold mb-4">
                        Jetzt bewerben
                    </div>
                    <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl text-moebus-deep leading-tight text-balance">
                        Schnell, einfach, mobil.
                    </h2>
                    <p className="mt-4 text-base md:text-lg text-slate-600">
                        Sende uns deine Bewerbung in wenigen Schritten.
                    </p>
                </div>

                <form
                    data-testid="application-form"
                    onSubmit={onSubmit}
                    className="max-w-3xl mx-auto bg-white border border-moebus-border rounded-lg p-6 md:p-10 shadow-sm"
                    noValidate
                >
                    {/* Honeypot */}
                    <div className="hidden" aria-hidden="true">
                        <label>Website</label>
                        <input
                            tabIndex={-1}
                            autoComplete="off"
                            value={form.website}
                            onChange={(e) => update("website", e.target.value)}
                        />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                            <label className="block text-sm font-medium text-moebus-deep mb-2">
                                Vorname <span className="text-moebus-green">*</span>
                            </label>
                            <input
                                data-testid="form-first-name"
                                type="text"
                                value={form.first_name}
                                onChange={(e) => update("first_name", e.target.value)}
                                className={inputCls("first_name")}
                                autoComplete="given-name"
                            />
                            {errors.first_name && (
                                <p className="text-xs text-red-600 mt-1">{errors.first_name}</p>
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-moebus-deep mb-2">
                                Nachname <span className="text-moebus-green">*</span>
                            </label>
                            <input
                                data-testid="form-last-name"
                                type="text"
                                value={form.last_name}
                                onChange={(e) => update("last_name", e.target.value)}
                                className={inputCls("last_name")}
                                autoComplete="family-name"
                            />
                            {errors.last_name && (
                                <p className="text-xs text-red-600 mt-1">{errors.last_name}</p>
                            )}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-moebus-deep mb-2">
                                Telefonnummer <span className="text-moebus-green">*</span>
                            </label>
                            <input
                                data-testid="form-phone"
                                type="tel"
                                value={form.phone}
                                onChange={(e) => update("phone", e.target.value)}
                                className={inputCls("phone")}
                                autoComplete="tel"
                            />
                            {errors.phone && <p className="text-xs text-red-600 mt-1">{errors.phone}</p>}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-moebus-deep mb-2">
                                E-Mail-Adresse <span className="text-moebus-green">*</span>
                            </label>
                            <input
                                data-testid="form-email"
                                type="email"
                                value={form.email}
                                onChange={(e) => update("email", e.target.value)}
                                className={inputCls("email")}
                                autoComplete="email"
                            />
                            {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-moebus-deep mb-2">
                                Wohnort <span className="text-moebus-green">*</span>
                            </label>
                            <input
                                data-testid="form-city"
                                type="text"
                                value={form.city}
                                onChange={(e) => update("city", e.target.value)}
                                className={inputCls("city")}
                                autoComplete="address-level2"
                            />
                            {errors.city && <p className="text-xs text-red-600 mt-1">{errors.city}</p>}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-moebus-deep mb-2">
                                Gewünschter Bereich <span className="text-moebus-green">*</span>
                            </label>
                            <select
                                data-testid="form-position"
                                value={form.position}
                                onChange={(e) => update("position", e.target.value)}
                                className={inputCls("position")}
                            >
                                <option value="">Bitte wählen…</option>
                                {positions.map((p) => (
                                    <option key={p} value={p}>
                                        {p}
                                    </option>
                                ))}
                            </select>
                            {errors.position && (
                                <p className="text-xs text-red-600 mt-1">{errors.position}</p>
                            )}
                        </div>
                    </div>

                    <div className="mt-6">
                        <label className="block text-sm font-medium text-moebus-deep mb-2">
                            Erzähle uns kurz etwas über dich
                        </label>
                        <textarea
                            data-testid="form-message"
                            value={form.message}
                            onChange={(e) => update("message", e.target.value)}
                            placeholder="Warum möchtest du bei uns arbeiten? Was kannst du besonders gut? Ab wann könntest du starten?"
                            rows={5}
                            className={`${inputCls("message")} resize-y`}
                        />
                    </div>

                    <div className="mt-8">
                        <label className="block text-sm font-medium text-moebus-deep mb-3">
                            Unterlagen hochladen
                        </label>
                        <div
                            {...getRootProps({
                                className: `cursor-pointer border-2 border-dashed rounded-lg p-8 md:p-12 text-center transition ${
                                    isDragActive
                                        ? "dropzone-active"
                                        : "border-slate-300 hover:border-moebus-green bg-slate-50"
                                }`,
                            })}
                            data-testid="dropzone"
                        >
                            <input {...getInputProps()} data-testid="file-input" />
                            <UploadCloud className="mx-auto text-moebus-green mb-3" size={36} strokeWidth={1.6} />
                            <div className="font-medium text-moebus-deep">
                                {isDragActive ? "Dateien hier ablegen…" : "Drag & Drop oder klicken zum Auswählen"}
                            </div>
                            <div className="text-xs text-slate-500 mt-2">
                                Bis zu {MAX_FILES} Dateien · max. 10 MB · PDF, DOC, DOCX, JPG, PNG
                            </div>
                        </div>

                        {files.length > 0 && (
                            <ul data-testid="file-list" className="mt-4 space-y-2">
                                {files.map((f, i) => {
                                    const Icon = fileIcon(f.name);
                                    return (
                                        <li
                                            key={i}
                                            data-testid={`file-item-${i}`}
                                            className="flex items-center gap-3 bg-white border border-moebus-border rounded-md px-4 py-3"
                                        >
                                            <Icon size={20} className="text-moebus-blue flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium text-moebus-deep truncate">
                                                    {f.name}
                                                </div>
                                                <div className="text-xs text-slate-500">{formatBytes(f.size)}</div>
                                            </div>
                                            <button
                                                type="button"
                                                data-testid={`remove-file-${i}`}
                                                onClick={() => removeFile(i)}
                                                className="text-slate-400 hover:text-red-600 transition-colors"
                                                aria-label="Datei entfernen"
                                            >
                                                <X size={18} />
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>

                    <div className="mt-8">
                        <label className="flex items-start gap-3 cursor-pointer">
                            <input
                                data-testid="form-privacy"
                                type="checkbox"
                                checked={form.privacy}
                                onChange={(e) => update("privacy", e.target.checked)}
                                className="mt-1 w-5 h-5 rounded border-moebus-border text-moebus-green focus:ring-moebus-green"
                            />
                            <span className="text-sm text-slate-700 leading-relaxed">
                                Ich bin damit einverstanden, dass meine Angaben und Dateien zur Bearbeitung meiner
                                Bewerbung gespeichert und verarbeitet werden. (Pflicht)
                            </span>
                        </label>
                        {errors.privacy && (
                            <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                                <AlertCircle size={14} /> {errors.privacy}
                            </p>
                        )}
                    </div>

                    <button
                        data-testid="form-submit"
                        type="submit"
                        disabled={submitting}
                        className="mt-8 w-full inline-flex items-center justify-center gap-2 bg-moebus-green hover:bg-emerald-700 disabled:bg-emerald-700/60 text-white font-medium px-6 py-4 rounded-md transition-colors text-base shadow-md"
                    >
                        {submitting ? (
                            <>
                                <Loader2 size={18} className="animate-spin" /> Wird gesendet…
                            </>
                        ) : (
                            "Bewerbung absenden"
                        )}
                    </button>
                </form>
            </div>
        </section>
    );
}
